import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import {
  AddedFailureReason,
  ChecklistItem,
  Message,
  MessageType,
  ScreeningResult,
  ScreeningStatus,
  TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import { useSnackbar } from 'notistack';
import useChat from '../../../hooks/use-chat';
import {
  buildCorrectionPayload,
  commentsByReason,
  passedReasonsOf,
  resolveReasonSet,
  toFailureReasonChips,
} from './helpers';
import ScreenView from './screen-view';
import { ScreeningCriteriaMap } from './types';
import useRateLimitedRefresh from './use-rate-limited-refresh';
import useScreeningCorrection from './use-screening-correction';

const ScreenContainer = () => {
  const { tripRequestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const queryParams = new URLSearchParams(location.search);
  const displayDraftMessage = queryParams.get('displayDraftMessage');

  const tripRequest = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);
  const screeningResult = useFetch<ScreeningResult>(apiClient.screeningResultsClient.getScreeningResult, [tripRequestId], !!tripRequestId, {
    shouldRetryOnError: (error: any) => {
      if (error.code === 404) return false;
      return true;
    },
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const conversationId = tripRequest.data?.travelerToZicassoId;
  const [submissionError, setSubmissionError] = useState<boolean>(false);
  const [isSavingFailureReasons, setIsSavingFailureReasons] = useState(false);
  const [isRegeneratingDraft, setIsRegeneratingDraft] = useState(false);
  const [draftResetKey, setDraftResetKey] = useState(0);
  const { remainingSeconds, refreshScreeningResult } = useRateLimitedRefresh({
    onRefresh: screeningResult.mutate as () => Promise<void>,
    rateLimitSeconds: 30,
  });
  const { data: screeningCriteriaMap } = useFetchImmutable<ScreeningCriteriaMap>(
    apiClient.screeningCriteriaClient.getScreeningCriteriaMap,
    [],
    true,
  );
  const handleCorrectionLoadError = (retry: () => void) => {
    enqueueSnackbar('Unable to load saved failure reasons. Please try again.', {
      variant: 'default',
      action: (
        <Button color="inherit" onClick={retry} size="small">
          Retry
        </Button>
      ),
    });
  };
  const {
    status,
    data,
    mutate,
    saveScreeningCorrection,
    regenerateScreeningDraft,
  } = useScreeningCorrection(screeningResult.data, handleCorrectionLoadError);

  const {
    sendMessage,
  } = useChat({
    conversationId,
  });

  const reset = () => {
    setSubmissionError(false);
  };

  const navigateBack = () => {
    navigate(-1);
  };

  const handleSubmit = async ({
    message,
  }: {
    message: string;
  }) => {
    reset();

    try {
      if (tripRequestId && conversationId) {
        const messageToSend: Partial<Message> = {
          conversationId,
          text: message,
          messageType: MessageType.Text,
        };

        await sendMessage(messageToSend);
        await apiClient.tripRequestsClient.deleteTripRequestChecklistItems(tripRequestId, {
          params: { item: [ChecklistItem.NeedsScreening, ChecklistItem.NeedsMatching] },
        });
        navigateBack();
        enqueueSnackbar('Message has been sent.', {
          variant: 'success',
          action: (
            <Button onClick={() => {
              navigate(`/trip-requests/${tripRequestId}/messages/${conversationId}`);
            }}
            >
              View
            </Button>
          ),
        });
      }
    } catch (e: any) {
      setSubmissionError(true);
    }
  };

  const isLoading = tripRequest.isLoading || screeningResult.isLoading;
  const error = tripRequest.error || (screeningResult.error && screeningResult.error.code !== 404);
  const screeningResultNotFound = screeningResult.error && screeningResult.error.code === 404;
  const isCorrectionLoading = status === 'loading';
  const fallbackReasons = screeningResult.data?.criteria
    ?.filter((criterion) => !criterion.passed)
    .map((criterion) => criterion.name) ?? [];
  const originalReasons = resolveReasonSet(
    isCorrectionLoading,
    data?.originalFailureReasons,
    fallbackReasons,
  );
  const effectiveReasons = resolveReasonSet(
    isCorrectionLoading,
    data?.effectiveFailureReasons,
    fallbackReasons,
  );
  const criteriaByName = useMemo(() => Object.fromEntries(
    (screeningResult.data?.criteria ?? []).map((criterion) => [criterion.name, criterion.result]),
  ), [screeningResult.data?.criteria]);
  const hasSentScreeningMessage = displayDraftMessage ? false : !tripRequest.data?.checklist?.includes(ChecklistItem.NeedsScreening);
  const defaultValues = useMemo(() => ({
    assignedTo: tripRequest.data?.assignedTo,
    message: data?.correction?.draftMessage ?? screeningResult.data?.draftMessage,
  }), [tripRequest.data, screeningResult.data, data?.correction?.draftMessage]);

  const persistFailureReasons = async (
    nextEffective: string[],
    commentOverrides: Record<string, string | undefined> = {},
  ) => {
    if (status !== 'ready' || isRegeneratingDraft) {
      return false;
    }

    setIsSavingFailureReasons(true);
    try {
      const nextState = await saveScreeningCorrection(
        buildCorrectionPayload(originalReasons, nextEffective, {
          ...commentsByReason(data?.correction?.addedFailureReasons),
          ...commentOverrides,
        }),
      );
      await mutate(nextState, { revalidate: false });
      enqueueSnackbar('Failure reasons updated.', { variant: 'success' });
      return true;
    } catch {
      enqueueSnackbar('Unable to update failure reasons. Please try again.', { variant: 'default' });
      return false;
    } finally {
      setIsSavingFailureReasons(false);
    }
  };

  const handleRemoveFailureReason = async (code: string) => {
    await persistFailureReasons(effectiveReasons.filter((reason) => reason !== code));
  };

  const handleRemoveAllFailureReasons = async () => {
    await persistFailureReasons([]);
  };

  const handleAddFailureReasons = async (drafts: AddedFailureReason[]) => {
    const nextEffective = [
      ...effectiveReasons,
      ...drafts.map((draft) => draft.reason).filter((code) => !effectiveReasons.includes(code)),
    ];
    return persistFailureReasons(
      nextEffective,
      Object.fromEntries(drafts.map((draft) => [draft.reason, draft.comment])),
    );
  };

  const handleRegenerateDraft = async () => {
    setIsRegeneratingDraft(true);
    try {
      const nextState = await regenerateScreeningDraft();
      await mutate(nextState, { revalidate: false });
      setDraftResetKey((current) => current + 1);
      enqueueSnackbar('Draft message regenerated.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Unable to regenerate draft. Please try again.', { variant: 'default' });
    } finally {
      setIsRegeneratingDraft(false);
    }
  };

  return (
    <ScreenView
      isLoading={isLoading}
      isError={Boolean(error)}
      tripRequest={tripRequest.data}
      screeningResult={screeningResult.data}
      screeningResultNotFound={screeningResultNotFound}
      hasSentScreeningMessage={hasSentScreeningMessage}
      onSubmit={handleSubmit}
      onCancel={navigateBack}
      submissionError={submissionError}
      refreshScreeningResultRemainingSeconds={remainingSeconds}
      refreshScreeningResult={refreshScreeningResult}
      defaultValues={defaultValues}
      canEditFailureReasons={status === 'ready'
        && !hasSentScreeningMessage
        && screeningResult.data?.status !== ScreeningStatus.ScreeningProcessing}
      screeningCriteriaMap={screeningCriteriaMap}
      failureReasons={toFailureReasonChips(
        effectiveReasons,
        originalReasons,
        criteriaByName,
        commentsByReason(data?.correction?.addedFailureReasons),
      )}
      passedReasons={passedReasonsOf(screeningResult.data?.criteria)}
      isCorrectionLoading={isCorrectionLoading}
      isSavingFailureReasons={isSavingFailureReasons}
      onAddFailureReasons={handleAddFailureReasons}
      onRemoveFailureReason={handleRemoveFailureReason}
      onRemoveAllFailureReasons={handleRemoveAllFailureReasons}
      canRegenerateDraft={!hasSentScreeningMessage}
      onRegenerateDraft={handleRegenerateDraft}
      isRegeneratingDraft={isRegeneratingDraft}
      draftResetKey={draftResetKey}
    />
  );
};

export default ScreenContainer;
